import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import noPropertiesPng from '@/assets/illustrations/empty/no-properties.png';

import { usePropertyStore } from '@/stores/property-store';
import { Button } from '@/components/common/Button';
import { PropertyCard } from '@/components/property/PropertyCard';
import { OperationalGuide } from '@/components/common/OperationalGuide';

export default function PropertiesPage() {
  const navigate = useNavigate();
  const { properties, isLoading, loadProperties, removeProperty } = usePropertyStore();

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    void loadProperties();
  }, [loadProperties]);

  async function handleDelete(id: string) {
    setIsDeleting(true);
    try {
      await removeProperty(id);
      setConfirmDeleteId(null);
    } finally {
      setIsDeleting(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-body-sm text-[var(--color-text-muted)]">Loading…</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 animate-fade-in" data-testid="properties-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Go back"
            data-testid="back-button"
            className="flex items-center justify-center -ml-1 p-1 rounded text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
          >
            <ChevronLeft size={20} />
          </button>
          <h1 className="font-display text-display-lg text-[var(--color-text-display)]">Properties &amp; Locations</h1>
        </div>
        {properties.length > 0 && (
          <Button onClick={() => void navigate('/properties/new')}>
            Add Property
          </Button>
        )}
      </div>

      {/* Delete confirmation dialog */}
      {confirmDeleteId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-delete-title"
        >
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl p-6 max-w-sm w-full shadow-[var(--shadow-card-md,var(--shadow-card))]">
            <h2 id="confirm-delete-title" className="font-display text-display-sm text-[var(--color-text-display)] mb-2">
              Delete property?
            </h2>
            <p className="text-body-sm text-[var(--color-text-secondary)] mb-5">
              This will permanently delete the property and all its locations, tasks, and scouting log entries.
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => setConfirmDeleteId(null)} disabled={isDeleting}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => void handleDelete(confirmDeleteId)}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting…' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {properties.length === 0 ? (
        <OperationalGuide
          illustration={noPropertiesPng}
          heading="Pin Your First Waypoint"
          subtext="Mark stands, plots, and trails to map your land."
          ctaLabel="Add your first property"
          onCtaClick={() => void navigate('/properties/new')}
          data-guide-id="properties-empty"
        />
      ) : (
        <div className="flex flex-col gap-3" data-testid="property-list">
          {properties.map((property) => (
            <PropertyCard
              key={property.id}
              property={property}
              onSelect={(id) => void navigate(`/properties/${id}`)}
              onDelete={(id) => setConfirmDeleteId(id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
